import React, { useEffect, useMemo, useState } from "react"
import styles from "./DualRangeSlider.module.scss"

type DualRangeSliderProps = {
  min: number
  max: number
  step?: number
  values: [number, number]
  onChange: (next: [number, number]) => void
  formatValue?: (value: number) => string
  formatLabels?: (values: [number, number]) => [string, string]
  presets?: Array<{ label: string; values: [number, number] }>
}

const clamp = (value: number, min: number, max: number) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step = 1,
  values,
  onChange,
  formatValue,
  formatLabels,
  presets,
}) => {
  const [localMin, setLocalMin] = useState(values[0])
  const [localMax, setLocalMax] = useState(values[1])

  useEffect(() => {
    setLocalMin(values[0])
    setLocalMax(values[1])
  }, [values])

  const handleMinChange = (nextValue: number) => {
    const limited = clamp(Math.min(nextValue, localMax - step), min, max)
    setLocalMin(limited)
    onChange([limited, Math.max(limited, localMax)])
  }

  const handleMaxChange = (nextValue: number) => {
    const limited = clamp(Math.max(nextValue, localMin + step), min, max)
    setLocalMax(limited)
    onChange([Math.min(localMin, limited), limited])
  }

  const handlePreset = (presetValues: [number, number]) => {
    const [presetMin, presetMax] = presetValues
    const nextMin = clamp(presetMin, min, max)
    const nextMax = clamp(presetMax, min, max)
    setLocalMin(nextMin)
    setLocalMax(nextMax)
    onChange([nextMin, nextMax])
  }

  const [startPercent, endPercent] = useMemo(() => {
    const range = max - min
    if (range <= 0) return [0, 0]
    const start = ((localMin - min) / range) * 100
    const end = ((localMax - min) / range) * 100
    return [start, end]
  }, [localMax, localMin, max, min])

  const trackStyle = {
    '--range-start': `${startPercent}%`,
    '--range-end': `${endPercent}%`,
  } as React.CSSProperties

  const [displayMin, displayMax] = useMemo(() => {
    if (formatLabels) return formatLabels([localMin, localMax])
    const formattedMin = formatValue ? formatValue(localMin) : localMin.toString()
    const formattedMax = formatValue ? formatValue(localMax) : localMax.toString()
    return [formattedMin, formattedMax]
  }, [formatLabels, formatValue, localMax, localMin])

  return (
    <div className={styles.wrapper}>
      {presets && presets.length > 0 && (
        <div className={styles.presets}>
          {presets.map((preset) => (
            <button key={preset.label} type="button" className={styles.preset} onClick={() => handlePreset(preset.values)}>
              {preset.label}
            </button>
          ))}
        </div>
      )}
      <div className={styles.sliderArea}>
        <div className={styles.inputs}>
          <label className={styles.inputField}>
            <span>Mínimo</span>
            <input
              type="number"
              value={localMin}
              min={min}
              max={localMax - step}
              step={step}
              onChange={(event) => handleMinChange(Number(event.target.value))}
            />
          </label>
          <span className={styles.separator}>–</span>
          <label className={styles.inputField}>
            <span>Máximo</span>
            <input
              type="number"
              value={localMax}
              min={localMin + step}
              max={max}
              step={step}
              onChange={(event) => handleMaxChange(Number(event.target.value))}
            />
          </label>
        </div>
        <div className={styles.sliderTrack} style={trackStyle}>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={localMin}
            onChange={(event) => handleMinChange(Number(event.target.value))}
          />
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={localMax}
            onChange={(event) => handleMaxChange(Number(event.target.value))}
          />
        </div>
      </div>
      <div className={styles.values}>
        <span>{displayMin}</span>
        <span>{displayMax}</span>
      </div>
    </div>
  )
}

export default DualRangeSlider
